import { createContext, ReactNode, useEffect, useState } from "react";
import { orgId } from "../utils/dashboardUtils";
import { metricsEnv } from "../utils/environment";

// Define the shape of your context data
interface MetricCategoriesContextType {
    metricCategories: string[];
    loading: boolean;
    fetchMetricCategoriesData: (orgId: number) => void;
}

// Create the context with a default value (empty categories and a no-op setter)
const MetricCategoriesContext = createContext<MetricCategoriesContextType>({
    metricCategories: [],
    loading: true,
    fetchMetricCategoriesData: () => {},
});

interface MetricCategoriesProviderProps {
    children: ReactNode;
}

// Define the provider for the context
export const MetricCategoriesProvider = ({ children }: MetricCategoriesProviderProps) => {
    const [metricCategories, setMetricCategories] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const fetchMetricCategoriesData = async (orgId: number) => {
        if (!orgId) {
            setLoading(false);
            console.log('No orgId provided to fetch metric metadata');
            return;
        }

        setLoading(true);
    
        try {
            const response = await fetch(`${import.meta.env.VITE_DASHBOARD_BACKEND_URL}/api/getmetriccategories?orgId=${orgId}&environment=${metricsEnv}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
    
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const result = await response.json();
            setMetricCategories(result || []);
        } catch (error: any) {
            console.error('Error fetching metric metadata:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMetricCategoriesData(orgId);
    }, []);

    return (
        <MetricCategoriesContext.Provider value={{ metricCategories, loading, fetchMetricCategoriesData }}>
            {children}
        </MetricCategoriesContext.Provider>
    );
};


// Export the hook to use the context
export default MetricCategoriesContext;